'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { create } from 'zustand';
import { CURRENCY_COOKIE } from '~/constants/cookies';
import { setCookie } from '~/utils/cookies';
import { CURRENCIES, type CurrencyName } from '~/utils/tpv2/currency';
const DATES_CONFIRMED_TRIP_KEY = 'byway.datesConfirmedTripId';
type TripBookingState = {
  activeTripId: string;
  setActiveTripId: (id: string) => void;
  travellerCount: number | null;
  rooms: number | null;
  datesConfirmedTripId: string | null;
  markDatesConfirmed: () => void;
  hydrateDatesConfirmed: () => void;
  initialize: (payload: {
    travellerCount: number;
    rooms: number;
  }) => void;
  setSelection: (payload: {
    travellerCount: number;
    rooms: number;
  }) => void;
  displayCurrency: CurrencyName;
  initializeCurrency: (currency: CurrencyName) => void;
  setDisplayCurrency: (currency: CurrencyName) => void;
};
const useTripBookingStore = create<TripBookingState>(set => ({
  activeTripId: '',
  setActiveTripId: id => set(state => state.activeTripId === id ? state : {
    activeTripId: id,
    travellerCount: null,
    rooms: null
  }),
  travellerCount: null,
  rooms: null,
  datesConfirmedTripId: null,
  markDatesConfirmed: () => set(state => {
    globalThis.sessionStorage?.setItem(DATES_CONFIRMED_TRIP_KEY, state.activeTripId);
    return {
      datesConfirmedTripId: state.activeTripId
    };
  }),
  hydrateDatesConfirmed: () => set({
    datesConfirmedTripId: globalThis.sessionStorage?.getItem(DATES_CONFIRMED_TRIP_KEY) ?? null
  }),
  initialize: ({
    travellerCount,
    rooms
  }) => set({
    travellerCount: Math.max(0, travellerCount),
    rooms: Math.max(1, rooms)
  }),
  setSelection: ({
    travellerCount,
    rooms
  }) => set({
    travellerCount: Math.max(0, travellerCount),
    rooms: Math.max(1, rooms)
  }),
  displayCurrency: CURRENCIES[0],
  // 'GBP' — overwritten on mount by TripBookingStoreInitializer
  initializeCurrency: currency => set({
    displayCurrency: currency
  }),
  setDisplayCurrency: currency => {
    setCookie(CURRENCY_COOKIE, currency, {
      maxAge: 60 * 60 * 24 * 180
    });
    set({
      displayCurrency: currency
    });
  }
}));
export { useTripBookingStore };
export function useTripBookingSelection(initialTravellerCount, initialRooms) {
  const $ = _c(9);
  const travellerCount = useTripBookingStore(_temp);
  const rooms = useTripBookingStore(_temp2);
  const initialize = useTripBookingStore(_temp3);
  const setSelection = useTripBookingStore(_temp4);
  let t0;
  let t1;
  if ($[0] !== initialRooms || $[1] !== initialTravellerCount || $[2] !== initialize) {
    t0 = () => {
      initialize({
        travellerCount: initialTravellerCount,
        rooms: initialRooms
      });
    };
    t1 = [initialTravellerCount, initialRooms, initialize];
    $[0] = initialRooms;
    $[1] = initialTravellerCount;
    $[2] = initialize;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const t2 = travellerCount ?? initialTravellerCount;
  const t3 = rooms ?? initialRooms;
  let t4;
  if ($[5] !== setSelection || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      travellerCount: t2,
      rooms: t3,
      setSelection
    };
    $[5] = setSelection;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp4(state_2) {
  return state_2.setSelection;
}
function _temp3(state_1) {
  return state_1.initialize;
}
function _temp2(state_0) {
  return state_0.rooms;
}
function _temp(state) {
  return state.travellerCount;
}